import { FaceLandmarker, FilesetResolver } from "@mediapipe/tasks-vision";
import {
  drawImageToCanvas,
  drawVideoToCanvas,
  FaceComparisonEngine,
} from "./FacialComparison";

const SMILES = [
  "/smiles/zuck1.jpg",
  "/smiles/zuck10.jpg",
  "/smiles/zuck13.jpg",
  "/smiles/zuck14.jpeg",
  "/smiles/zuck17.jpg",
  "/smiles/zuck2.png",
  "/smiles/zuck20.jpeg",
  "/smiles/zuck3.png",
  "/smiles/zuck4.jpg",
  "/smiles/zuck5.jpg",
  "/smiles/zuck8.jpg",
  "/smiles/zuck9.jpg",
  "/smiles/zuckcage.png",
  "/smiles/zuckdanawhite.png",
  "/smiles/zuckdownlow.jpeg",
  "/smiles/zuckfrustrated.jpg",
  "/smiles/zuckglasses.jpeg",
  "/smiles/zuckpost.jpeg",
  "/smiles/zuckpresent.jpg",
  "/smiles/zuckshout.jpg",
  "/smiles/zuckwiki.jpg",
];

const NOT_SMILES = [
  "/not-smiles/zuck12.jpeg",
  "/not-smiles/zuck15.jpg",
  "/not-smiles/zuck16.jpg",
  "/not-smiles/zuck18.png",
  "/not-smiles/zuck19.png",
  "/not-smiles/zuck21.jpg",
  "/not-smiles/zuck6.jpg",
  "/not-smiles/zuck7.jpeg",
  "/not-smiles/zuckderp.jpg",
  "/not-smiles/zuckhoodie.jpeg",
  "/not-smiles/zuckhospital.jpg",
  "/not-smiles/zuckjail.jpg",
  "/not-smiles/zucksad.jpg",
  "/not-smiles/zuckserious.jpeg",
  "/not-smiles/zuckstare.jpg",
  "/not-smiles/zuckthink.jpg",
];

const MIN_DETECTION_CONFIDENCE = 0.5;
const MIN_TRACKING_CONFIDENCE = 0.5;
const MIN_SUPPRESSION_THRESHOLD = 0.5;
const MAX_PER_FRAME_SCORE = 0.9;
const MIN_SCORE_FOR_COUNTS = 0.5;
const IMAGE_TIMEOUT_TIME = 5000; // 5000
const TIME_BETWEEN_FACE_ADDITIONS = 1000;
export const TIME_TO_COMPLETE_IMAGE_WITH_PERFECT_SCORE = 1250;
export const GAME_DURATION_MS = 30000; //30000  30 seconds game duration

async function _createVideoLandmarker() {
  const filesetResolver = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm"
  );

  async function getVideoLandmarker() {
    return FaceLandmarker.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
        delegate: "GPU",
      },
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      runningMode: "VIDEO",
      numFaces: 1,
      minFaceDetectionConfidence: MIN_DETECTION_CONFIDENCE,
      minTrackingConfidence: MIN_TRACKING_CONFIDENCE,
      minSuppressionThreshold: MIN_SUPPRESSION_THRESHOLD,
    });
  }
  return getVideoLandmarker();
}

async function _createImageLandmarker() {
  const filesetResolver = await FilesetResolver.forVisionTasks(
    "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.21/wasm"
  );

  async function getImageLandmarker() {
    return FaceLandmarker.createFromOptions(filesetResolver, {
      baseOptions: {
        modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
        delegate: "GPU",
      },
      outputFaceBlendshapes: true,
      outputFacialTransformationMatrixes: true,
      runningMode: "IMAGE",
      numFaces: 1,
      minFaceDetectionConfidence: MIN_DETECTION_CONFIDENCE,
      minTrackingConfidence: MIN_TRACKING_CONFIDENCE,
      minSuppressionThreshold: MIN_SUPPRESSION_THRESHOLD,
    });
  }
  return getImageLandmarker();
}

class SUBSCRIPTIONS {
  static LANDMARKER_LOADED = "landmarkerLoaded";
  static WEBCAM_BLENDSHAPES = "webcamBlendshapes";
  static BLENDSHAPE_SIMILARITY = "blendshapeSimilarity";
  static COMPLETED = "completed";
  static SCORE = "score";
  static GAME_STATE_CHANGED = "gameStateChanged";
  static RESULTS = "results";
  static TIME_REMAINING = "timeRemaining";
}

class GAME_STATES {
  static FIRST_PAGE_LOAD = "firstPageLoad";
  static LOADING_VIDEO = "loadingVideo";
  static LOADING_LANDMARKERS = "loadingLandmarkers";
  static COUNTING_DOWN = "countingDown";
  static RUNNING_GAME = "runningGame";
  static COMPUTING_RESULTS = "computingResults";
  static SHOWING_RESULTS = "showingResults";
}

class MediapipeInterface {
  constructor() {
    this._landmarkersLoaded = false;
    this._landmarkers = {
      video: null,
      image: null,
    };
    this.targetVideo = null;
    this.consumers = {};
    this.gameState = GAME_STATES.FIRST_PAGE_LOAD;
    this.setInitialState();
    this.comparisonEngine = new FaceComparisonEngine();
  }

  setInitialState() {
    this.imageBlendshapes = [];
    this.imageLandmarks = [];
    this.imageTransformationMatrixes = null;
    this.currentIdx = null;
    this.imageSelectedAt = null;
    this.roundStartTime = null;
    this.completed = false;
    this.recentScores = [];
    this.shuffledSmiles = this.makeShuffledImages({ smiles: true });
    this.shuffledNotSmiles = this.makeShuffledImages({ smiles: false });
    this.imageState = { showSmile: true, smileIdx: 0, notSmileIdx: 0 };
    this.canBeginAt = null;
    this.pickingImage = false;
    this.currentScore = 0;
    this.imagesCompleted = 0;
    this.bestTwoFaces = [];
    this.beganResultComputation = false;
    this.lastFaceAddTime = null;
    this.pickedFirstImage = false;
  }

  makeShuffledImages({ smiles }) {
    const target = smiles ? SMILES : NOT_SMILES;
    const shuffled = [...target];
    shuffled.sort(() => Math.random() - 0.5);
    return shuffled;
  }

  subscribe(type, id, consumer) {
    if (!this.consumers[type]) {
      this.consumers[type] = [];
    }
    this.consumers[type].push({ consumer, id });
  }

  unsubscribe(type, id) {
    if (!this.consumers[type]) {
      return;
    }
    this.consumers[type] = this.consumers[type].filter((c) => c.id !== id);
  }

  broadcast(type, data) {
    if (!this.consumers[type]) {
      return;
    }
    this.consumers[type].forEach((c) => c.consumer(data));
  }

  setGameState(gameState) {
    this.gameState = gameState;
    this.broadcast(SUBSCRIPTIONS.GAME_STATE_CHANGED, gameState);
  }

  runNewGame() {
    this.setInitialState();
    this.setGameState(GAME_STATES.COUNTING_DOWN);
  }

  async loadLandmarkers() {
    this.setGameState(GAME_STATES.LOADING_LANDMARKERS);
    this._landmarkers.video = await _createVideoLandmarker();
    this._landmarkers.image = await _createImageLandmarker();
    this._landmarkersLoaded = true;
    this.broadcast(SUBSCRIPTIONS.LANDMARKER_LOADED, true);
    this.setGameState(GAME_STATES.COUNTING_DOWN);
  }

  getImageSrc() {
    if (this.imageState.showSmile) {
      return this.shuffledSmiles[this.imageState.smileIdx];
    } else {
      return this.shuffledNotSmiles[this.imageState.notSmileIdx];
    }
  }

  incrementImageState() {
    if (this.imageState.showSmile) {
      this.imageState.smileIdx++;
      this.imageState.showSmile = false;
      if (this.imageState.smileIdx >= this.shuffledSmiles.length) {
        this.imageState.smileIdx = 0;
        this.shuffledSmiles = this.makeShuffledImages({ smiles: true });
      }
    } else {
      this.imageState.notSmileIdx++;
      this.imageState.showSmile = true;
      if (this.imageState.notSmileIdx >= this.shuffledNotSmiles.length) {
        this.imageState.notSmileIdx = 0;
        this.shuffledNotSmiles = this.makeShuffledImages({ smiles: false });
      }
    }
  }

  pickNextImage({ image, imageCanvas }) {
    const imageSrc = this.getImageSrc();
    image.src = imageSrc;
    this.pickingImage = true;
    image.onload = () => {
      const landmarks = this._landmarkers.image.detect(image);

      if (landmarks && landmarks.faceLandmarks && landmarks.faceLandmarks[0]) {
        drawImageToCanvas(image, landmarks.faceLandmarks[0], imageCanvas);
      }
      if (
        landmarks &&
        landmarks.faceBlendshapes &&
        landmarks.faceBlendshapes[0]
      ) {
        this.imageBlendshapes = landmarks.faceBlendshapes[0].categories;
        this.imageLandmarks = landmarks.faceLandmarks[0];
        this.imageTransformationMatrixes =
          landmarks.facialTransformationMatrixes[0];
      } else {
        console.log("NO BLENDSHAPES", landmarks);
      }

      this.completed = false;
      this.pickingImage = false;
      const now = performance.now();
      this.imageSelectedAt = now;
    };
  }

  completeImage({ success, startTime }) {
    if (success) {
      this.imagesCompleted++;
    }
    const timeToComplete = startTime - this.imageSelectedAt;
    this.broadcast(SUBSCRIPTIONS.COMPLETED, { success, timeToComplete });
    this.broadcast(SUBSCRIPTIONS.SCORE, success ? 1 : this.currentScore);
    this.completed = true;
    this.incrementImageState();
    this.currentScore = 0;
  }

  maybeDrawVideo({ video, videoCanvas, result }) {
    if (result && result.faceLandmarks && result.faceLandmarks[0]) {
      drawVideoToCanvas(video, result.faceLandmarks[0], videoCanvas);
    }
  }

  addPoints({ score, elapsed }) {
    let points;
    if (score > MAX_PER_FRAME_SCORE) {
      points = 1;
    } else if (score > MIN_SCORE_FOR_COUNTS) {
      points = score;
    } else {
      points = 0;
    }
    points *= elapsed / TIME_TO_COMPLETE_IMAGE_WITH_PERFECT_SCORE;
    this.currentScore = Math.min(1, this.currentScore + points);
    if (this.currentScore >= 1) {
      return { completed: true, totalScore: this.currentScore };
    }
    return { completed: false, totalScore: this.currentScore };
  }

  updateBestFaces({ score, videoCanvas, imageCanvas, imageSrc }) {
    if (
      this.lastFaceAddTime !== null &&
      performance.now() - this.lastFaceAddTime < TIME_BETWEEN_FACE_ADDITIONS
    ) {
      return;
    }
    this.lastFaceAddTime = performance.now();

    const getFaceMatch = () => {
      const userFace = videoCanvas.toDataURL("image/png");
      const zuckFace = imageCanvas.toDataURL("image/png");

      return {
        score,
        userFace,
        zuckFace,
        timestamp: Date.now(),
        imageSrc,
      };
    };

    if (this.bestTwoFaces.length < 2) {
      console.log(
        "adding first face",
        this.bestTwoFaces.length,
        score,
        imageSrc
      );
      this.bestTwoFaces.push(getFaceMatch());
      this.bestTwoFaces.sort((a, b) => b.score - a.score);
    } else {
      let added = false;
      if (
        score > this.bestTwoFaces[1].score &&
        imageSrc !== this.bestTwoFaces[0].imageSrc
      ) {
        added = true;
        this.bestTwoFaces[1] = getFaceMatch();
      } else if (
        score > this.bestTwoFaces[0].score &&
        imageSrc === this.bestTwoFaces[0].imageSrc
      ) {
        added = true;
        this.bestTwoFaces[0] = getFaceMatch();
      }
      if (added) {
        console.log(
          "added good face",
          this.bestTwoFaces.length,
          score,
          imageSrc
        );
        this.bestTwoFaces.sort((a, b) => b.score - a.score);
      }
    }
  }

  async generateComparisonImage() {
    if (this.bestTwoFaces.length < 2) return Promise.resolve("");

    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 800;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const loadImages = () => {
      return Promise.all(
        this.bestTwoFaces.flatMap((face, index) => {
          const zuckImg = new Image();
          const userImg = new Image();

          return [
            new Promise((resolve) => {
              zuckImg.onload = () =>
                resolve({ img: zuckImg, type: "zuck", index });
              zuckImg.src = face.zuckFace;
            }),
            new Promise((resolve) => {
              userImg.onload = () =>
                resolve({ img: userImg, type: "user", index });
              userImg.src = face.userFace;
            }),
          ];
        })
      );
    };

    return loadImages().then((images) => {
      images.forEach(({ img, type, index }) => {
        const x = type === "zuck" ? 0 : 300;
        const y = index * 400;

        if (type === "zuck") {
          ctx.drawImage(img, x, y, 300, 400);
        } else {
          ctx.save();
          ctx.translate(x + 300, y);
          ctx.scale(-1, 1);
          ctx.drawImage(img, 0, 0, 300, 400);
          ctx.restore();
        }
      });

      ctx.font = "24px monospace";
      ctx.fillStyle = "#fde68a";
      ctx.strokeStyle = "#3730a3";
      ctx.lineWidth = 4;
      ctx.textAlign = "right";
      let rightText = "smilelikezuck.com";
      ctx.strokeText(rightText, 570, 760);
      ctx.fillText(rightText, 570, 760);

      ctx.textAlign = "left";
      let leftText = `score: ${this.imagesCompleted} zucks`;
      ctx.strokeText(leftText, 30, 760);
      ctx.fillText(leftText, 30, 760);
      return canvas.toDataURL("image/png");
    });
  }

  computeResults() {
    if (this.beganResultComputation) return;
    this.beganResultComputation = true;
    this.broadcast(SUBSCRIPTIONS.SCORE, 0);
    this.generateComparisonImage().then((comparisonImage) => {
      this.broadcast(SUBSCRIPTIONS.RESULTS, {
        comparisonImage,
        imagesCompleted: this.imagesCompleted,
      });
      this.setGameState(GAME_STATES.SHOWING_RESULTS);
    });
  }

  addScoreAndMaybeComplete({
    startTime,
    result,
    elapsed,
    videoCanvas,
    imageCanvas,
  }) {
    if (this.completed || this.pickingImage) {
      return;
    }

    if (result && result.faceBlendshapes && result.faceBlendshapes[0]) {
      const { score, uncappedScore, screenshotScore } =
        this.comparisonEngine.computeScore({
          zuckBlendshapes: this.imageBlendshapes,
          usBlendshapes: result.faceBlendshapes[0].categories,
          zuckLandmarks: this.imageLandmarks,
          usLandmarks: result.faceLandmarks[0],
          zuckTransformationMatrixes: this.imageTransformationMatrixes,
          usTransformationMatrixes: result.facialTransformationMatrixes[0],
        });

      // Update best faces tracking with current score and canvas states
      this.updateBestFaces({
        score: uncappedScore * screenshotScore,
        videoCanvas,
        imageCanvas,
        imageSrc: this.getImageSrc(),
      });

      const { completed, totalScore } = this.addPoints({
        score,
        elapsed,
      });
      if (completed) {
        this.completeImage({ success: true, startTime });
      } else {
        this.broadcast(SUBSCRIPTIONS.SCORE, totalScore);
      }
    }
  }

  runWebcamLoop({ video, videoCanvas, image, imageCanvas }) {
    this.targetVideo = video;
    let lastVideoTime = -1;

    function loop() {
      const startTime = performance.now();
      if (
        this.gameState === GAME_STATES.COMPUTING_RESULTS &&
        !this.beganResultComputation
      ) {
        this.computeResults.bind(this)();
      }

      if (
        this.gameState !== GAME_STATES.RUNNING_GAME &&
        this.gameState !== GAME_STATES.COUNTING_DOWN
      ) {
        requestAnimationFrame(loop.bind(this));
        return;
      }

      if (
        this.gameState === GAME_STATES.RUNNING_GAME &&
        this.roundStartTime !== null &&
        startTime - this.roundStartTime >= GAME_DURATION_MS
      ) {
        this.setGameState(GAME_STATES.COMPUTING_RESULTS);
        requestAnimationFrame(loop.bind(this));
        return;
      }

      let result = null;
      let elapsed = 0;

      if (video.currentTime !== lastVideoTime) {
        elapsed = lastVideoTime === -1 ? 0 : startTime - lastVideoTime;
        lastVideoTime = startTime;
        result = this._landmarkers.video.detectForVideo(video, startTime);
        this.maybeDrawVideo({ video, videoCanvas, result });
      }

      if (this.gameState === GAME_STATES.COUNTING_DOWN) {
        if (!this.pickedFirstImage) {
          this.pickedFirstImage = true;
          this.pickNextImage({ image, imageCanvas });
        } else if (this.canBeginAt === null) {
          this.canBeginAt = startTime + 1000;
        } else if (startTime >= this.canBeginAt) {
          this.setGameState(GAME_STATES.RUNNING_GAME);
          this.roundStartTime = startTime;
        }
      } else if (this.gameState === GAME_STATES.RUNNING_GAME) {
        let timeRemaining =
          GAME_DURATION_MS - (startTime - this.roundStartTime);
        timeRemaining /= GAME_DURATION_MS;
        this.broadcast(SUBSCRIPTIONS.TIME_REMAINING, timeRemaining);

        if (this.completed && !this.pickingImage) {
          this.pickNextImage({ image, imageCanvas });
        } else if (!this.completed && !this.pickingImage) {
          if (
            this.imageSelectedAt !== null &&
            startTime - this.imageSelectedAt > IMAGE_TIMEOUT_TIME
          ) {
            this.completeImage({ success: false, startTime });
          } else {
            let sinceLastImagePicked = startTime - this.imageSelectedAt;
            const elapsedOrPicked = Math.min(sinceLastImagePicked, elapsed);
            this.addScoreAndMaybeComplete({
              startTime,
              result,
              elapsed: elapsedOrPicked,
              videoCanvas,
              imageCanvas,
            });
          }
        }
      }

      requestAnimationFrame(loop.bind(this));
    }
    requestAnimationFrame(loop.bind(this));
  }
}

export default MediapipeInterface;
export { SUBSCRIPTIONS, GAME_STATES };
